// User Manager page (WP-15).
// Team roster, role assignments, member status, and invitations matching WhatChimp §1.8.

import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../lib/i18n';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/ConfirmDialog';
import {
  PlusIcon,
  TrashIcon,
  CheckIcon,
} from '../components/icons';
import {
  memberList,
  memberSetRole,
  roleList,
  type MemberDoc,
  type RoleDoc,
} from '../lib/permissions';
import { useTeam, removeMember } from '../lib/team';
import { useAuth } from '../lib/useAuth';
import { useQuotas } from '../lib/usage';
import { formatRelativeTime } from '../lib/connectHelpers';
import { InviteMemberModal } from '../components/team/InviteMemberModal';

const PALETTE = ['bg-brand', 'bg-blue-500', 'bg-amber-500', 'bg-rose-500', 'bg-emerald-600', 'bg-indigo-500'];
function colorFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

function initialsFor(name: string, email: string): string {
  const target = name || email.split('@')[0] || '?';
  return (target.slice(0, 2) || '?').toUpperCase();
}

export default function UserManager() {
  const { t } = useI18n();
  const toast = useToast();
  const confirm = useConfirm();
  const { user } = useAuth();
  const { invites } = useTeam();
  const seatLimit = useQuotas().quotas.teamSeats;

  const [members, setMembers] = useState<MemberDoc[]>([]);
  const [roles, setRoles] = useState<RoleDoc[]>([]);
  const [loading, setLoading] = useState(true);

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [busyUid, setBusyUid] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [membersRes, rolesRes] = await Promise.all([
        memberList({}),
        roleList({}),
      ]);
      if (membersRes.members) setMembers(membersRes.members);
      if (rolesRes.roles) setRoles(rolesRes.roles);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
    // `toast` deliberately excluded: an error toast must not re-run the loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  async function handleRoleChange(memberUid: string, newRoleId: string) {
    setBusyUid(memberUid);
    try {
      const res = await memberSetRole({ uid: memberUid, roleId: newRoleId });
      if (res.ok) {
        setMembers((prev) =>
          prev.map((m) => (m.uid === memberUid ? { ...m, roleId: newRoleId } : m))
        );
        toast.success(t('team.roleUpdated'));
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyUid(null);
    }
  }

  async function handleRemoveMember(m: MemberDoc) {
    if (m.role === 'owner') return;
    const name = m.displayName || m.email;
    if (!(await confirm({
      title: `${t('common.remove')} ${name}`,
      body: t('team.removeConfirm', { name }),
      confirmLabel: t('common.remove'),
      danger: true,
    }))) {
      return;
    }
    setBusyUid(m.uid);
    try {
      await removeMember({ uid: m.uid });
      toast.success(t('team.memberRemoved'));
      await loadData();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyUid(null);
    }
  }

  function renderLastLogin(ts: number | null, ip: string | null) {
    if (!ts) return t('team.never');
    const rel = formatRelativeTime(ts);
    const timeStr = rel.params ? t(rel.key).replace('{n}', String(rel.params.n)) : t(rel.key);
    return (
      <span title={ip ? `IP: ${ip}` : undefined}>
        {timeStr}
        {ip && <span className="ms-1 font-mono text-[10px] text-ink-3">({ip})</span>}
      </span>
    );
  }

  const usedSeats = members.length;
  const isFull = usedSeats >= seatLimit;

  return (
    <div className="space-y-6 pb-12">
      {/* Page Header */}
      <PageHeader
        title={t('team.title')}
        subtitle={t('team.subtitle')}
        action={
          <Button
            size="sm"
            disabled={isFull}
            onClick={() => setInviteModalOpen(true)}
          >
            <PlusIcon size={14} className="me-1.5" />
            {t('team.inviteMember')}
          </Button>
        }
      />

      {/* Seats usage info banner */}
      <div className="flex items-center justify-between rounded-card border border-line bg-surface/30 p-4 dark:border-slate-800 dark:bg-slate-800/30">
        <div className="flex items-center gap-2 text-xs font-semibold text-ink dark:text-slate-200">
          <span>{t('team.seatUsage')}:</span>
          <Badge variant={isFull ? 'danger' : 'brand'} size="sm">
            {t('team.seats', { used: usedSeats, total: seatLimit })}
          </Badge>
        </div>
        {isFull && (
          <span className="text-xs text-danger dark:text-rose-400 font-medium">
            {t('team.seatsFullWarning')}
          </span>
        )}
      </div>

      {/* Member Roster Table */}
      <div className="overflow-hidden rounded-card border border-line bg-white shadow-card dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-line bg-surface/30 px-4 py-3 sm:px-5 dark:border-slate-800 dark:bg-slate-800/30">
          <h2 className="text-sm font-extrabold text-ink dark:text-slate-100">
            {t('team.activeMembers')} ({members.length})
          </h2>
        </div>

        {loading ? (
          <div className="grid h-48 place-items-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-xs">
              <thead className="border-b border-line bg-surface/50 text-ink-3 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3 text-start font-semibold">{t('team.member')}</th>
                  <th className="px-4 py-3 text-start font-semibold">{t('team.role')}</th>
                  <th className="px-4 py-3 text-start font-semibold">{t('team.status')}</th>
                  <th className="px-4 py-3 text-start font-semibold">{t('team.assignedSubscribers')}</th>
                  <th className="px-4 py-3 text-start font-semibold">{t('team.phoneVerified')}</th>
                  <th className="px-4 py-3 text-start font-semibold">{t('team.lastLogin')}</th>
                  <th className="px-4 py-3 text-end font-semibold">{t('team.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line dark:divide-slate-800">
                {members.map((m) => {
                  const isSelf = m.uid === user?.uid;
                  const isOwner = m.role === 'owner';
                  const isBusy = m.uid === busyUid;
                  const avatarColor = colorFor(m.uid);
                  const initials = initialsFor(m.displayName || '', m.email);

                  return (
                    <tr
                      key={m.uid}
                      className="hover:bg-surface/30 dark:hover:bg-slate-800/20 transition-colors"
                    >
                      {/* Avatar & Name */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span
                            className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-bold text-white ${avatarColor}`}
                          >
                            {initials}
                          </span>
                          <div>
                            <div className="flex items-center gap-1.5 font-bold text-ink dark:text-slate-100">
                              <span>{m.displayName || m.email.split('@')[0]}</span>
                              {isSelf && (
                                <Badge variant="brand" size="sm">
                                  {t('team.you')}
                                </Badge>
                              )}
                            </div>
                            <span className="block text-xs text-ink-3 dark:text-slate-400">
                              {m.email}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Role Dropdown */}
                      <td className="px-4 py-3">
                        {isOwner ? (
                          <Badge variant="brand" size="sm">
                            {t('roles.owner')}
                          </Badge>
                        ) : (
                          <select
                            value={m.roleId}
                            disabled={isBusy || isSelf}
                            onChange={(e) => void handleRoleChange(m.uid, e.target.value)}
                            className="rounded-[7px] border border-line bg-white px-2 py-1 text-xs text-ink outline-none focus:border-brand disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                          >
                            {roles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <Badge variant="success" size="sm" dot>
                          {t('team.active')}
                        </Badge>
                      </td>

                      {/* Subscribers Assigned */}
                      <td className="px-4 py-3 font-semibold text-ink-2 dark:text-slate-300">
                        {m.subscribersAssigned}
                      </td>

                      {/* Phone Verified */}
                      <td className="px-4 py-3">
                        {m.phoneVerified ? (
                          <span className="inline-flex items-center gap-1 text-success font-semibold text-[11px]">
                            <CheckIcon size={12} />
                            {t('team.verified')}
                          </span>
                        ) : (
                          <span className="text-ink-3 text-[11px]">
                            {t('team.unverified')}
                          </span>
                        )}
                      </td>

                      {/* Last Login & IP */}
                      <td className="px-4 py-3 text-ink-3 dark:text-slate-400 whitespace-nowrap">
                        {renderLastLogin(m.lastLoginAt, m.lastLoginIp)}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-end">
                        {!isOwner && !isSelf && (
                          <Button
                            size="xs"
                            variant="ghost"
                            disabled={isBusy}
                            onClick={() => void handleRemoveMember(m)}
                            className="text-danger hover:bg-danger-50 hover:text-danger dark:hover:bg-rose-950/40"
                            title={t('team.remove')}
                          >
                            <TrashIcon size={13} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending Invites Section */}
      {invites.length > 0 && (
        <div className="overflow-hidden rounded-card border border-line bg-white shadow-card dark:border-slate-800 dark:bg-slate-900">
          <div className="border-b border-line bg-surface/30 px-4 py-3 sm:px-5 dark:border-slate-800 dark:bg-slate-800/30">
            <h2 className="text-sm font-extrabold text-ink dark:text-slate-100">
              {t('team.pendingInvites')} ({invites.length})
            </h2>
          </div>
          <div className="divide-y divide-line dark:divide-slate-800">
            {invites.map((inv) => (
              <div
                key={inv.id}
                className="flex items-center justify-between gap-3 p-4 sm:px-5 text-xs"
              >
                <div>
                  <span className="font-semibold text-ink dark:text-slate-200">{inv.email}</span>
                  <div className="mt-0.5 flex items-center gap-2 text-ink-3 dark:text-slate-400">
                    <span>{t('team.invitedAs', { role: inv.role })}</span>
                  </div>
                </div>
                <Badge variant="warning" size="sm" dot>
                  {t('team.pending')}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      <InviteMemberModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        roles={roles}
        onInvited={loadData}
      />
    </div>
  );
}
